import { useState } from 'react'
import { removeProviderKey, saveProviderKey, setDraftProvider } from '../api/settings'
import type { LlmProvider, MeResponse } from '../types'
import { Linked } from '../components/Linked'

/**
 * Which model writes the reply — and the only place the app asks for an API key.
 *
 * Two steps, deliberately: a model cannot be selected until its key is saved,
 * because a selection with no key behind it fails at the ticket — where the
 * agent has a customer waiting — instead of here, where the fix is one field
 * away. The backend enforces the same order.
 *
 * All three options are equal here. Gemini used to have a second card of its
 * own because it also embedded the knowledge base; that runs on the server's
 * key now, so picking Gemini costs an agent exactly one key, same as the rest.
 */
export function DraftModelSection({
  me,
  onChanged,
}: {
  me: MeResponse | null
  onChanged: () => void
}) {
  const active = me?.draftProvider ?? 'gemini'
  const [pick, setPick] = useState<LlmProvider>(active)
  const [key, setKey] = useState('')
  const [busy, setBusy] = useState('')
  const [error, setError] = useState('')
  const [done, setDone] = useState('')

  const catalogue = me?.providerCatalogue ?? []
  const chosen = catalogue.find((p) => p.id === pick)
  const state = me?.providers?.[pick]
  const hasKey = Boolean(state?.valid)
  // A key the provider has since rejected is still a key we are holding, and
  // still the one to get rid of — so removal goes by `set`, not by `valid`.
  const savedKey = Boolean(state?.set)
  const isActive = pick === active

  function choose(next: LlmProvider) {
    setPick(next)
    setKey('')
    setError('')
    setDone('')
  }

  async function run(what: string, fn: () => Promise<unknown>) {
    setBusy(what)
    setError('')
    setDone('')
    try {
      await fn()
      onChanged()
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e))
    } finally {
      setBusy('')
    }
  }

  const saveKey = () =>
    run('key', async () => {
      await saveProviderKey(pick, key.trim())
      setKey('')
      // Saving a key for a model you just picked is only ever a step towards
      // using it, so don't make the agent press a second button for that.
      if (!isActive) await setDraftProvider(pick)
    })

  /**
   * Hand the key back.
   *
   * Removing the key of the model in use moves the choice to another model
   * that has one, so say which — the dropdown's "(in use)" marker moving on
   * its own is not an answer to "what writes my replies now".
   */
  const removeKey = () =>
    run('remove', async () => {
      const res = await removeProviderKey(pick)
      setKey('')
      const now = catalogue.find((p) => p.id === res.draftProvider)?.label ?? res.draftProvider
      setDone(
        res.switched
          ? `Key removed. ${now} writes your replies now.`
          : `${chosen?.label} key removed.`,
      )
    })

  const activate = () => run('switch', () => setDraftProvider(pick))

  return (
    <div className="card">
      <h2>Reply model {me?.providers?.[active]?.valid && <span className="ok">✓</span>}</h2>
      <p className="hint">Generates your replies. Your key, only you see it.</p>

      {error && <div className="error"><Linked text={error} /></div>}
      {done && <p className="note done">{done}</p>}

      <label className="field">
        <span>Which model writes your replies</span>
        <select
          value={pick}
          disabled={Boolean(busy)}
          onChange={(e) => choose(e.target.value as LlmProvider)}
        >
          {catalogue.map((p) => (
            <option key={p.id} value={p.id}>
              {p.label} · {p.model}
              {p.id === active ? ' (in use)' : ''}
            </option>
          ))}
        </select>
      </label>

      {/* The saved key shows as its own placeholder, in green. An empty box
          under a model that plainly works reads as a key gone missing, and a
          sentence saying "key saved — ending in iki2" was a third way of
          telling the agent the same thing. */}
      <label className="field">
        <span>
          {chosen?.label} API key
          {hasKey && <span className="saved-tag">saved</span>}
        </span>
        <input
          type="password"
          className={hasKey && !key.trim() ? 'key-ok' : undefined}
          placeholder={
            hasKey ? `${'•'.repeat(14)}${state?.hint ?? ''}` : (chosen?.keyPlaceholder ?? '')
          }
          value={key}
          onChange={(e) => setKey(e.target.value)}
          autoComplete="off"
        />
      </label>

      {(!hasKey || key.trim().length > 0) && (
        <button disabled={Boolean(busy) || key.trim().length < 10} onClick={saveKey}>
          {busy === 'key' ? 'Checking…' : hasKey ? 'Update key' : `Save ${chosen?.label} key`}
        </button>
      )}

      {!isActive && hasKey && (
        <button disabled={Boolean(busy)} onClick={activate}>
          {busy === 'switch' ? 'Switching…' : `Use ${chosen?.label}`}
        </button>
      )}

      {savedKey && (
        <button className="linkish danger" disabled={Boolean(busy)} onClick={removeKey}>
          {busy === 'remove' ? 'Removing…' : `Remove ${chosen?.label} key`}
        </button>
      )}

      {/* The catalogue stores the address without a scheme, because that is
          how it reads to a person. href needs one. */}
      {!hasKey && chosen && (
        <p className="note">
          Get a key at{' '}
          <a href={`https://${chosen.keyUrl}`} target="_blank" rel="noreferrer noopener">
            {chosen.keyUrl}
          </a>
          {chosen.keyHint && <span className="key-hint">{chosen.keyHint}</span>}
        </p>
      )}

      {chosen && !chosen.supportsPdf && (
        <p className="note">{chosen.label} cannot read PDFs or images on a ticket.</p>
      )}
    </div>
  )
}
